import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Download, Send, CheckCircle2, Loader2, Image as ImageIcon, Menu, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DemoResults = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [designBrief, setDesignBrief] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!location.state?.formData) {
      navigate('/demo');
      return;
    }

    const formData = location.state.formData;
    const brief = generateDesignBrief(formData);
    setDesignBrief(brief);
    
    // Generate image after brief is created
    generateFashionImage(brief);
  }, [location, navigate]);

  const generateDesignBrief = (formData) => {
    const {
      intentionalOrientation,
      culturalReferences,
      sustainabilityPriority,
      climateContext,
      communicationGoal
    } = formData;

    let brief = {
      primaryColourLogic: '',
      recommendedTextile: '',
      silhouetteStructure: '',
      layeringStrategy: '',
      sustainabilityConsideration: '',
      rationale: ''
    };

    // Rule-based logic implementation
    const sustainability = sustainabilityPriority[0];
    const hasYoruba = culturalReferences.includes('yoruba');
    const hasIgbo = culturalReferences.includes('igbo');
    const hasGhanaian = culturalReferences.includes('ghanaian');
    const hasContemporary = culturalReferences.includes('contemporary');
    const hasCulturalRef = hasYoruba || hasIgbo || hasGhanaian || hasContemporary;

    // Primary Colour Logic
    if (intentionalOrientation === 'professional-authority') {
      brief.primaryColourLogic = 'Dark or neutral palette base (charcoal, navy, deep brown) to establish authority and command respect. Minimal accent colors for controlled sophistication.';
    } else if (intentionalOrientation === 'cultural-celebration') {
      brief.primaryColourLogic = 'Rich, vibrant cultural palette with bold primary colors. Traditional color symbolism from selected cultural references to celebrate heritage authentically.';
    } else if (intentionalOrientation === 'ceremonial') {
      brief.primaryColourLogic = 'Regal and symbolic colors (gold, deep purple, ivory) appropriate for ceremonial significance. Colors chosen to reflect the gravity and importance of the occasion.';
    } else if (intentionalOrientation === 'visible-statement') {
      brief.primaryColourLogic = 'Bold, attention-commanding colors with high contrast. Strategic use of unexpected color combinations to create memorable visual impact.';
    } else {
      brief.primaryColourLogic = 'Warm, grounded earth tones (sage, taupe, terracotta) for understated elegance. Subtle depth through tonal variation rather than bold contrast.';
    }

    // Recommended Textile
    if (climateContext === 'uk-winter' && hasCulturalRef) {
      if (hasYoruba) {
        brief.recommendedTextile = 'Layered Aso Oke with wool blend lining for UK winter adaptation. Traditional hand-woven textile reinforced with modern thermal properties for climate appropriateness.';
      } else {
        brief.recommendedTextile = 'Heavyweight cultural textiles (Kente, George) with thermal lining. Wool-blend backing or quilted interlining to maintain cultural authenticity while ensuring warmth.';
      }
    } else if (climateContext === 'uk-winter') {
      brief.recommendedTextile = 'Premium wool, cashmere blends, or heavyweight cotton twill. Textiles with natural insulation properties and structured drape for cold weather sophistication.';
    } else if (climateContext === 'uk-summer') {
      brief.recommendedTextile = 'Breathable natural fibers: lightweight cotton, linen, or silk blends. Moisture-wicking properties and airy weave for UK summer comfort.';
    } else if (climateContext === 'indoor-formal') {
      brief.recommendedTextile = 'Fine wool, silk, or premium cotton suiting. Climate-controlled indoor environment allows focus on drape, sheen, and formal presentation.';
    } else {
      brief.recommendedTextile = 'Mid-weight natural fibers with layering versatility. Cotton-wool blends or lightweight wool for transitional UK weather (spring/autumn).';
    }

    // Silhouette Structure
    if (intentionalOrientation === 'professional-authority') {
      brief.silhouetteStructure = 'Structured tailoring with defined shoulders and clean lines. Sharp, architectural silhouette that conveys competence and control through precise fit.';
    } else if (intentionalOrientation === 'cultural-celebration' && hasCulturalRef) {
      brief.silhouetteStructure = 'Traditional silhouette adapted for contemporary UK context. Flowing volumes and cultural draping techniques modified for practical movement and modern sensibility.';
    } else if (intentionalOrientation === 'ceremonial') {
      brief.silhouetteStructure = 'Grand, formal silhouette with intentional volume. Structured foundation with ceremonial presence—commanding attention through scale and proportion.';
    } else if (intentionalOrientation === 'visible-statement') {
      brief.silhouetteStructure = 'Bold, unconventional silhouette with unexpected proportions. Asymmetry, dramatic volumes, or sculptural elements that disrupt conventional expectations.';
    } else {
      brief.silhouetteStructure = 'Relaxed, fluid silhouette with gentle structure. Soft tailoring that suggests confidence without rigidity—elegant ease rather than formality.';
    }

    // Layering Strategy
    if (climateContext === 'uk-winter') {
      brief.layeringStrategy = 'Multi-layer approach with thermal base, insulating mid-layer, and protective outer layer. Structured coat or jacket over layered garments for both warmth and visual interest.';
    } else if (climateContext === 'uk-spring-autumn') {
      brief.layeringStrategy = 'Transitional layering with removable pieces. Light jacket, cardigan, or shawl over core outfit to adapt to fluctuating temperatures throughout the day.';
    } else if (climateContext === 'uk-summer') {
      brief.layeringStrategy = 'Minimal layering—single or two-layer maximum. Lightweight outer layer (linen jacket, silk kimono) for sun protection or indoor air conditioning, easily removed.';
    } else {
      brief.layeringStrategy = 'Optional layering for climate-controlled indoor environment. Focus on single-layer statement piece or strategic layering for visual texture rather than thermal function.';
    }

    // Sustainability Consideration
    if (sustainability >= 4) {
      brief.sustainabilityConsideration = 'Prioritize durable, long-lasting textiles with neutral color base for multi-season, multi-use adaptability. Timeless design elements that transcend trends. Investment in quality over quantity—garments designed for years of wear. Consider textile longevity, repairability, and ethical sourcing.';
    } else if (sustainability === 3) {
      brief.sustainabilityConsideration = 'Balance between trend responsiveness and durability. Select versatile pieces with moderate longevity—seasonal relevance without fast-fashion disposability. Natural fibers preferred where practical.';
    } else {
      brief.sustainabilityConsideration = 'Flexibility for trend engagement with some consideration for textile quality. Shorter wear cycle acceptable—focus on immediate impact and current aesthetic rather than long-term wardrobe investment.';
    }

    // Rationale
    let rationalePoints = [];

    rationalePoints.push(`Your selected intentional orientation of "${formatOrientation(intentionalOrientation)}" guides the overall tone—${getRationaleForOrientation(intentionalOrientation)}`);

    if (hasCulturalRef) {
      const culturalNames = [];
      if (hasYoruba) culturalNames.push('Yoruba (Aso Oke)');
      if (hasIgbo) culturalNames.push('Igbo (George)');
      if (hasGhanaian) culturalNames.push('Ghanaian (Kente)');
      if (hasContemporary) culturalNames.push('Contemporary African fusion');
      rationalePoints.push(`Cultural references (${culturalNames.join(', ')}) are woven into textile and silhouette choices, ensuring respectful representation adapted for UK climate and contemporary context.`);
    }

    rationalePoints.push(`The ${formatClimate(climateContext)} context directly shapes textile weight, layering strategy, and practical wearability.`);

    if (sustainability >= 4) {
      rationalePoints.push(`Your high sustainability priority (${sustainability}/5) drives recommendations toward durable, timeless pieces with multi-season versatility and long-term wardrobe value.`);
    } else if (sustainability <= 2) {
      rationalePoints.push(`Your preference for trend flexibility (${sustainability}/5) allows for more fashion-forward, seasonally responsive choices.`);
    }

    rationalePoints.push(`Your stated goal—"${communicationGoal.substring(0, 100)}${communicationGoal.length > 100 ? '...' : ''}"—informs the entire brief, ensuring design choices align with your intended message.`);

    brief.rationale = rationalePoints.join(' ');

    return brief;
  };

  const formatOrientation = (orientation) => {
    return orientation.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const formatClimate = (climate) => {
    const climateMap = {
      'uk-winter': 'UK winter',
      'uk-spring-autumn': 'UK spring/autumn',
      'uk-summer': 'UK summer',
      'indoor-formal': 'indoor formal event'
    };
    return climateMap[climate] || climate;
  };

  const getRationaleForOrientation = (orientation) => {
    const rationaleMap = {
      'professional-authority': 'structured, commanding presence with minimal distraction',
      'cultural-celebration': 'vibrant, authentic expression of heritage and identity',
      'subtle-confidence': 'understated elegance with quiet self-assurance',
      'visible-statement': 'bold, memorable impact that commands attention',
      'ceremonial': 'formal grandeur appropriate for significant occasions'
    };
    return rationaleMap[orientation] || 'intentional presentation';
  };

  const generateFashionImage = async (brief) => {
    setIsGeneratingImage(true);
    setImageError(null);
    
    try {
      const response = await axios.post(`${API}/generate-design-image`, {
        design_brief: brief
      }, {
        timeout: 90000 // 90 second timeout for image generation
      });
      
      if (response.data.success && response.data.image_base64) {
        setGeneratedImage(`data:image/png;base64,${response.data.image_base64}`);
        toast.success('Design visual generated successfully!');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      setImageError('Unable to generate design visual. Please try again.');
      toast.error('Failed to generate design visual');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const handlePrintPDF = () => {
    window.print();
    toast.success('Print dialog opened');
  };

  const handleSendToStudio = () => {
    // Mock functionality
    console.log('Design brief sent to studio:', designBrief);
    toast.success('Design brief sent to studio! We\'ll be in touch soon.');
  };

  if (!designBrief) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <p className="text-[#B8927C]">Loading your design brief...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#FAF9F7]/95 backdrop-blur-sm border-b border-[#E8C4AD] print:hidden">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-serif font-bold text-[#D47B5E]">
              FashionClarity
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-4">
              <Link to="/demo">
                <Button variant="ghost" className="text-[#D47B5E]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Form
                </Button>
              </Link>
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-md text-[#D47B5E] hover:bg-[#F9E5D9] transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
          
          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-[#E8C4AD]">
              <Link to="/demo" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="text-[#D47B5E] w-full justify-start">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Form
                </Button>
              </Link>
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="text-[#D47B5E] w-full justify-start mt-2">
                  Back to Home
                </Button>
              </Link>
            </div>
          )}
        </nav>
      </header>

      {/* Results Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle2 className="w-12 h-12 text-[#E8B298]" />
            </div>
            <h1 className="text-4xl font-serif font-light text-[#D47B5E] mb-4">
              Your Design Brief
            </h1>
            <p className="text-lg text-[#2C2C2C]">
              A structured design intelligence report tailored to your needs
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8 print:hidden">
            <Button
              onClick={handlePrintPDF}
              className="flex-1 bg-white border-2 border-[#D47B5E] text-[#D47B5E] hover:bg-[#F9E5D9]"
            >
              <Download className="w-4 h-4 mr-2" />
              Print / Export PDF
            </Button>
            <Button
              onClick={handleSendToStudio}
              className="flex-1 bg-[#D47B5E] hover:bg-[#B8614A] text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Send to Studio
            </Button>
          </div>

          {/* Design Brief Card */}
          <Card className="bg-white border-[#E8C4AD] shadow-lg">
            <CardHeader>
              <CardTitle className="text-3xl font-serif text-[#D47B5E]">
                Structured Design Brief
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              
              {/* Primary Colour Logic */}
              <div className="border-b border-[#E8C4AD] pb-6">
                <h3 className="text-xl font-semibold text-[#D47B5E] mb-3">
                  Primary Colour Logic
                </h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  {designBrief.primaryColourLogic}
                </p>
              </div>

              {/* Recommended Textile */}
              <div className="border-b border-[#E8C4AD] pb-6">
                <h3 className="text-xl font-semibold text-[#D47B5E] mb-3">
                  Recommended Textile
                </h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  {designBrief.recommendedTextile}
                </p>
              </div>

              {/* Silhouette Structure */}
              <div className="border-b border-[#E8C4AD] pb-6">
                <h3 className="text-xl font-semibold text-[#D47B5E] mb-3">
                  Silhouette Structure
                </h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  {designBrief.silhouetteStructure}
                </p>
              </div>

              {/* Layering Strategy */}
              <div className="border-b border-[#E8C4AD] pb-6">
                <h3 className="text-xl font-semibold text-[#D47B5E] mb-3">
                  Layering Strategy
                </h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  {designBrief.layeringStrategy}
                </p>
              </div>

              {/* Sustainability Consideration */}
              <div className="border-b border-[#E8C4AD] pb-6">
                <h3 className="text-xl font-semibold text-[#D47B5E] mb-3">
                  Sustainability Consideration
                </h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  {designBrief.sustainabilityConsideration}
                </p>
              </div>

              {/* Rationale */}
              <div>
                <h3 className="text-xl font-semibold text-[#D47B5E] mb-3">
                  Rationale
                </h3>
                <p className="text-[#2C2C2C] leading-relaxed">
                  {designBrief.rationale}
                </p>
              </div>

            </CardContent>
          </Card>

          {/* Design Visual Section */}
          <Card className="mt-8 bg-white border-[#E8C4AD] shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-serif text-[#D47B5E] flex items-center">
                <ImageIcon className="w-6 h-6 mr-3 text-[#E8B298]" />
                Design Visual
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isGeneratingImage ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-4">
                  <Loader2 className="w-12 h-12 text-[#E8B298] animate-spin" />
                  <p className="text-[#B8927C] text-lg">Generating your design visual...</p>
                  <p className="text-[#B8927C] text-sm">This may take up to 60 seconds</p>
                </div>
              ) : imageError ? (
                <div className="flex flex-col items-center justify-center py-16 space-y-4 bg-[#F9E5D9] rounded-lg">
                  <ImageIcon className="w-12 h-12 text-[#B8927C]" />
                  <p className="text-[#B8927C] text-center">{imageError}</p>
                  <Button
                    onClick={() => generateFashionImage(designBrief)}
                    className="bg-[#D47B5E] hover:bg-[#B8614A] text-white"
                  >
                    Try Again
                  </Button>
                </div>
              ) : generatedImage ? (
                <div className="space-y-4">
                  <img
                    src={generatedImage}
                    alt="Generated fashion design visual"
                    className="w-full rounded-lg shadow-md"
                  />
                  <p className="text-sm text-[#B8927C] text-center italic">
                    AI-generated visual representation of your design brief
                  </p>
                </div>
              ) : null}
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="mt-8 bg-[#F9E5D9] border-[#E8C4AD] print:hidden">
            <CardContent className="pt-6">
              <h3 className="text-xl font-semibold text-[#D47B5E] mb-4">Next Steps</h3>
              <ul className="space-y-2 text-[#2C2C2C]">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Save or print this brief for your records</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Share with your stylist, tailor, or bespoke studio</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Use as a reference when shopping for garments or textiles</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Return to create additional briefs for different occasions</span>
                </li>
              </ul>
            </CardContent>
          </Card>

        </div>
      </section>
    </div>
  );
};

export default DemoResults;
